import { useState } from 'react';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import interviewTechnicalChallengesService from '@/services/interviewTechnicalChallenges';
import { InterviewTechnicalChallenge } from '@/types/api';
import EditTechnicalChallengeModalComponent from './EditTechnicalChallengeModal.component';

interface EditTechnicalChallengeModalContainerProps {
  isOpen: boolean;
  onClose: () => void;
  challenge: InterviewTechnicalChallenge;
  onChallengeUpdated?: (updatedChallenge: InterviewTechnicalChallenge) => void;
  onReviewersChanged?: () => void;
}

const EditTechnicalChallengeModalContainer = ({
  isOpen,
  onClose,
  challenge,
  onChallengeUpdated,
  onReviewersChanged,
}: EditTechnicalChallengeModalContainerProps): JSX.Element => {
  const toast = useToastContext();
  const [isLoading, setIsLoading] = useState(false);

  const updateChallengeMutation = useApiSubmit<
    InterviewTechnicalChallenge,
    {
      interviewTechnicalChallengeId: string;
      interviewTechnicalChallenge: Partial<InterviewTechnicalChallenge>;
    }
  >(interviewTechnicalChallengesService.editInterviewTechnicalChallenge);

  const handleSave = async (
    updatedChallenge: Partial<InterviewTechnicalChallenge>
  ): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await updateChallengeMutation.mutateAsync({
        interviewTechnicalChallengeId: challenge.id,
        interviewTechnicalChallenge: updatedChallenge,
      });

      toast.showSuccessFeedback('Technical challenge updated successfully.');

      if (onChallengeUpdated) {
        onChallengeUpdated(response);
      }

      onClose();
    } catch (error) {
      toast.showErrorFeedback('Failed to update technical challenge. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <EditTechnicalChallengeModalComponent
      isOpen={isOpen}
      onClose={onClose}
      challenge={challenge}
      onSave={handleSave}
      onReviewersChanged={onReviewersChanged}
      isLoading={isLoading}
    />
  );
};

export default EditTechnicalChallengeModalContainer;
